import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import virtualTryOnService from '../services/virtualTryOnService';
import { selectHasPersonImage } from '../redux/selectors/personImageSelectors';
import usePersonImages from './usePersonImages';

/**
 * Custom hook for managing virtual try-on mode state
 * Handles mode switching, availability checking, and state persistence
 */
export const useTryOnMode = (initialMode = true) => {
  // Default to virtual mode (true) instead of manual
  const [isVirtualMode, setIsVirtualMode] = useState(initialMode);
  const [isAvailable, setIsAvailable] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  // Get person image from Redux store
  const hasPersonImage = useSelector(selectHasPersonImage);

  // Get actual person image data from backend integration
  const { activePersonImage } = usePersonImages();
  // Memoize personImage to prevent unnecessary re-renders
  const personImage = useMemo(() => activePersonImage?.image?.url || null, [activePersonImage?.image?.url]);

  /**
   * Check if virtual try-on is available based on person image
   */
  const checkAvailability = useCallback(async (image = personImage) => {
    if (!image) {
      setIsAvailable(false);
      setAvailabilityChecked(true);
      return false;
    }

    try {
      const availability = await virtualTryOnService.checkAvailability(image);
      setIsAvailable(availability.available);
      setAvailabilityChecked(true);

      return availability.available;
    } catch (error) {
      console.error('Error checking virtual try-on availability:', error);
      setIsAvailable(false);
      setAvailabilityChecked(true);

      return false;
    }
  }, [personImage]);

  /**
   * Update person image and check availability
   * Note: This is now handled by Redux, but kept for compatibility
   */
  const updatePersonImage = useCallback(async (image) => {
    // Person image is now managed by Redux
    // This function is kept for backward compatibility
    setAvailabilityChecked(false);

    if (image || hasPersonImage) {
      await checkAvailability(image || personImage);
    } else {
      setIsAvailable(false);
      setAvailabilityChecked(true);
    }
  }, [checkAvailability, hasPersonImage, personImage]);



  /**
   * Toggle between virtual and manual mode
   */
  const toggleMode = useCallback((newMode) => {
    if (newMode !== undefined) {
      setIsVirtualMode(newMode);
      return newMode;
    }
    
    setIsVirtualMode(prev => !prev);
    return !isVirtualMode;
  }, [isVirtualMode]);

  /**
   * Force refresh availability check
   */
  const refreshAvailability = useCallback(async () => {
    setAvailabilityChecked(false);
    return await checkAvailability();
  }, [checkAvailability]);

  /**
   * Get current mode information
   */
  const getModeInfo = useCallback(() => {
    return {
      isVirtualMode,
      isAvailable,
      canSwitchToVirtual: isAvailable,
      canSwitchToManual: true,
      currentModeLabel: isVirtualMode ? 'Virtual Try-On' : 'Manual Positioning',
      availabilityStatus: availabilityChecked
        ? (isAvailable ? 'available' : 'unavailable')
        : 'checking'
    };
  }, [isVirtualMode, isAvailable, availabilityChecked]);

  /**
   * Get user-friendly status message
   */
  const getStatusMessage = useCallback(() => {
    //Don't show "checking" message if already in virtual mode
    if (!availabilityChecked && !isVirtualMode) {
      return 'Checking virtual try-on availability...';
    }

    if (isVirtualMode) {
      return personImage
        ? 'Virtual try-on is active. Click clothing items to try them on.'
        : 'Virtual try-on is not available. Switched to manual mode.';
    } else {
      return personImage
        ? 'Manual mode is active. You can switch to virtual try-on.'
        : 'Manual mode is active. Upload a person image to enable virtual try-on.';
    }
  }, [isVirtualMode, personImage, availabilityChecked]);

  // HARDCODED: Skip loading saved preference, always use VTO mode

  // Check availability when person image changes
  useEffect(() => {
    if (personImage) {
      // If person image exists, set as available immediately
      setIsAvailable(true);
      setAvailabilityChecked(true);
    } else {
      // No person image, not available but don't auto-switch modes
      // Let user stay in VTO mode so they can upload image
      setIsAvailable(false);
      setAvailabilityChecked(true);
    }
  }, [personImage]);

  return {
    // State
    isVirtualMode,
    isAvailable,
    personImage,
    availabilityChecked,

    // Actions
    toggleMode,
    updatePersonImage,
    checkAvailability,
    refreshAvailability,

    // Utilities
    getModeInfo,
    getStatusMessage,

    // Direct setters
    setIsVirtualMode,
    setIsAvailable,

    // Redux state
    hasPersonImage: !!personImage
  };
};

/**
 * Hook for managing mode state with local storage persistence
 */
export const usePersistentTryOnMode = (storageKey = 'virtualTryOnMode') => {
  const [isVirtualMode, setIsVirtualMode] = useState(false);

  const toggleMode = useCallback(() => {
    setIsVirtualMode(prev => !prev);
    return !isVirtualMode;
  }, [isVirtualMode]);

  return {
    isVirtualMode,
    toggleMode,
    setIsVirtualMode
  };
};

/**
 * Hook for managing mode state with session storage (temporary)
 */
export const useSessionTryOnMode = (storageKey = 'sessionVirtualTryOnMode') => {
  const [isVirtualMode, setIsVirtualMode] = useState(false);

  const toggleMode = useCallback(() => {
    setIsVirtualMode(prev => !prev);
    return !isVirtualMode;
  }, [isVirtualMode]);

  return {
    isVirtualMode,
    toggleMode,
    setIsVirtualMode
  };
};

export default useTryOnMode;